import { notificationApi } from 'Api/notification';
import { Checkbox, Switch, Table, Tooltip } from 'antd';
import Column from 'antd/es/table/Column';
import Buttons from 'components/Global/Buttons';
import { useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import RoleAssignModal from '../AssignRoleModal';
import styles from './index.module.scss';
import './index.scss';
import { EyeIcon } from 'utils/svgs';
import { ExclamationCircleOutlined } from '@ant-design/icons';

const NotificationBody = () => {
	const [notificationData, setNotificationData] = useState<any[]>([]);
	const [isRoleModalOpen, setIsRoleModalOpen] = useState<boolean>(false);
	const [selectedNotification, setSelectedNotification] = useState<any>(null);
	const [roles, setRoles] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [moduleName, setModuleName] = useState<string>('');
	const [saveLoading, setSaveLoading] = useState(false);

	const fetchNotificationConfig = async () => {
		try {
			setIsLoading(true);
			const notificationConfig =
				await notificationApi.getNotificationConfig();

			setNotificationData(notificationConfig.data.data);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message || 'Failed to fetch notifications';
			toastText(errorMessage, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	const handleNotificationSubmit = async () => {
		try {
			// ✅ Updated validation: Only check for first approver
			const dualApprovalModules = notificationData.filter(
				(item: any) =>
					item.requiresDualApproval &&
					(item.moduleName === 'Payroll' ||
						item.moduleName === 'Salary')
			);

			for (const module of dualApprovalModules) {
				// Only check if first approvers have at least one checked role
				const hasFirstApprover = (module.firstApproverRoles || []).some(
					(role: any) => role?.isChecked
				);

				if (!hasFirstApprover) {
					toastText(
						`Please select at least one First Approver for ${module.moduleName}`,
						'error'
					);
					return; // Stop submission
				}
			}

			// If validation passes, proceed with save
			setSaveLoading(true);
			await notificationApi.updateNotificationConfig({
				data: notificationData,
			});

			toastText('Notification settings saved', 'success');
			await fetchNotificationConfig();
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message || 'Failed to save settings';
			toastText(errorMessage, 'error');
		} finally {
			setSaveLoading(false);
		}
	};

	useEffect(() => {
		fetchNotificationConfig();
	}, []);

	const handleNotificationConfig = (
		checked: any,
		id: string,
		type: string
	) => {
		const _data = notificationData.map((item: any) => {
			if (item.id === id) {
				item[type] = checked;
				if (type === 'approvalRequired' && checked) {
					item['pushNotification'] = true;
				}
				if (type === 'pushNotification' && !checked) {
					if (
						item.moduleName === 'Time Sheets' ||
						item.moduleName === 'Leave Request'
					) {
						item['approvalRequired'] = true;
					} else {
						item['approvalRequired'] = false;
					}
				}
			}
			return item;
		});
		setNotificationData(_data);
	};

	const handleRoleSubmit = (notificationId: string, roleData: any) => {
		const data = notificationData.map((item: any) => {
			if (item.id === notificationId) {
				// Check if roleData has dual approval structure
				if (
					roleData.firstApproverRoles &&
					roleData.secondApproverRoles
				) {
					return {
						...item,
						firstApproverRoles: roleData.firstApproverRoles,
						secondApproverRoles: roleData.secondApproverRoles,
						roles: roleData.firstApproverRoles,
					};
				} else {
					// Single approval mode
					return {
						...item,
						roles: roleData,
						firstApproverRoles: roleData,
						secondApproverRoles: [],
					};
				}
			} else {
				return item;
			}
		});
		setNotificationData(data);
		setIsRoleModalOpen(false);
	};

	const buttons = [
		{
			text: 'Save',
			isSubmit: true,
			className: 'btn-blue',
			onclick: () => {
				handleNotificationSubmit();
			},
			disabled: isLoading,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isLoading: saveLoading,
		},
	];

	return (
		<div className={styles['notification-settings']}>
			<Table
				dataSource={notificationData}
				pagination={false}
				loading={isLoading}
				scroll={{
					x: 1400,
					y: 'calc(100vh - 275px)',
				}}
				rowKey="id"
			>
				<Column
					title="Module Name"
					dataIndex="parentModuleName"
					key="parentModuleName"
					className="bg-white"
					width={180}
					fixed="left"
					render={(text: string, record: any, index: number) => {
						const previousRecord = notificationData[index - 1];
						if (
							!previousRecord ||
							previousRecord.parentModuleName !==
								record.parentModuleName
						) {
							return {
								children: text,
								props: {
									rowSpan: notificationData.filter(
										(item) =>
											item.parentModuleName ===
											record.parentModuleName
									).length,
								},
							};
						}
						return {
							children: text,
							props: {
								rowSpan: 0,
							},
						};
					}}
				/>
				<Column
					title="Sub Module Name"
					dataIndex="moduleName"
					key="moduleName"
					className="bg-white"
					width={180}
				/>
				<Column
					title="Triggers"
					dataIndex="trigger"
					key="trigger"
					className="bg-white"
					width={150}
					render={(_data: any, record: any) => {
						const activeTriggers = [];

						if (record.isAdd) activeTriggers.push('Add');
						if (record.isEdit) activeTriggers.push('Edit');
						if (record.isDelete) activeTriggers.push('Delete');

						return activeTriggers.join('/');
					}}
				/>

				<Column
					title="Push"
					dataIndex="pushNotification"
					key="pushNotification"
					className="bg-white"
					width={80}
					align="center"
					render={(record: any, data: any) => {
						return (
							<Checkbox
								checked={record}
								disabled={data.pushDisable}
								onChange={(e) =>
									handleNotificationConfig(
										e.target.checked,
										data.id,
										'pushNotification'
									)
								}
							/>
						);
					}}
				/>
				<Column
					title="Email"
					dataIndex="emailNotification"
					key="emailNotification"
					className="bg-white"
					width={80}
					align="center"
					render={(record: any, data: any) => {
						return (
							<Checkbox
								checked={record}
								disabled={data.emailDisable}
								onChange={(e) =>
									handleNotificationConfig(
										e.target.checked,
										data.id,
										'emailNotification'
									)
								}
							/>
						);
					}}
				/>
				<Column
					title="Approval"
					dataIndex="approvalRequired"
					key="approvalRequired"
					className="bg-white"
					width={100}
					align="center"
					render={(record: any, data: any) => {
						return (
							<Checkbox
								checked={record}
								disabled={data.approvalDisable}
								onChange={(e) =>
									handleNotificationConfig(
										e.target.checked,
										data.id,
										'approvalRequired'
									)
								}
							/>
						);
					}}
				/>

				<Column
					title="Approval Roles"
					dataIndex="roles"
					key="roles"
					className="bg-white"
					width={180}
					fixed="right"
					render={(record: any, data: any) => {
						return (
							<div
								style={{
									display: 'flex',
									gap: '10px',
									alignItems: 'center',
								}}
							>
								<RoleToolTip data={data} />

								<p
									className="pointer color-blue"
									style={{
										margin: 0,
										whiteSpace: 'nowrap',
									}}
									onClick={() => {
										setIsRoleModalOpen(true);
										setSelectedNotification(data);
										setRoles(data.roles);
										setModuleName(data.moduleName);
									}}
								>
									Assign Role
								</p>
							</div>
						);
					}}
				/>
				<Column
					title="Dual Approval"
					dataIndex="requiresDualApproval"
					key="requiresDualApproval"
					className="bg-white"
					width={130}
					fixed="right"
					align="center"
					render={(record: any, data: any) => {
						if (
							data.moduleName === 'Payroll' ||
							data.moduleName === 'Salary'
						) {
							const tooltipText = record
								? 'Dual approval is enabled. Two levels of approval are required to approve changes'
								: 'Single-level approval. Only one approver is required to approve changes';

							return (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										gap: '8px',
									}}
								>
									<Switch
										checked={record}
										disabled={false}
										onChange={(checked) =>
											handleNotificationConfig(
												checked,
												data.id,
												'requiresDualApproval'
											)
										}
									/>
									<Tooltip
										title={tooltipText}
										placement="top"
									>
										<ExclamationCircleOutlined
											style={{
												color: '#584495',
												fontSize: '16px',
												cursor: 'pointer',
											}}
										/>
									</Tooltip>
								</div>
							);
						}
						return '-';
					}}
				/>
			</Table>
			<Buttons buttons={buttons} />
			{isRoleModalOpen && (
				<RoleAssignModal
					isOpen={isRoleModalOpen}
					handleCancel={() => setIsRoleModalOpen(false)}
					selectedNotification={selectedNotification}
					handleRoleSubmit={handleRoleSubmit}
					roleData={roles}
					moduleName={moduleName}
				/>
			)}
		</div>
	);
};

export default NotificationBody;

// ✅ Fixed RoleToolTip Component with proper error handling
const RoleToolTip = ({ data }: any) => {
	const hasDualApproval =
		data.requiresDualApproval &&
		(data.moduleName === 'Payroll' || data.moduleName === 'Salary');

	if (hasDualApproval) {
		const firstApproverRoles = (data.firstApproverRoles || [])
			.filter((role: any) => role?.isChecked)
			.map((role: any) => role.roleName);

		const secondApproverRoles = (data.secondApproverRoles || [])
			.filter((role: any) => role?.isChecked)
			.map((role: any) => role.roleName);

		const firstApproversDisplay = [...firstApproverRoles].filter(
			(role, index, self) => self.indexOf(role) === index
		);

		const secondApproversDisplay = ['Admin', ...secondApproverRoles].filter(
			(role, index, self) => self.indexOf(role) === index
		);

		return (
			<div className="ant-tooltip-container">
				<Tooltip
					title={
						<div>
							<div style={{ marginBottom: '8px' }}>
								<strong>First Approvers:</strong>
								<div>
									{firstApproversDisplay.length > 0
										? firstApproversDisplay.join(', ')
										: 'None'}
								</div>
							</div>
							<div>
								<strong>Second Approvers:</strong>
								<div>
									{secondApproversDisplay.length > 0
										? secondApproversDisplay.join(', ')
										: 'Admin'}
								</div>
							</div>
						</div>
					}
					placement="top"
					overlayInnerStyle={{ width: 'max-content' }}
					className="ant-tooltip-paye"
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							cursor: 'pointer',
						}}
					>
						<EyeIcon />
					</div>
				</Tooltip>
			</div>
		);
	} else {
		const checkedRoles = (data.roles || [])
			.filter((role: any) => role?.isChecked)
			.map((role: any) => role.roleName);

		const rolesDisplay = ['Admin', ...checkedRoles].filter(
			(role, index, self) => self.indexOf(role) === index
		);

		return (
			<div className="ant-tooltip-container">
				<Tooltip
					title={rolesDisplay.join(', ')}
					placement="top"
					overlayInnerStyle={{ width: 'max-content' }}
					className="ant-tooltip-paye"
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							cursor: 'pointer',
						}}
					>
						<EyeIcon />
					</div>
				</Tooltip>
			</div>
		);
	}
};
